import React, { useState } from "react";
import { Upload, BarChart3 } from "lucide-react";
import SearchInput from "../components/SearchInput";
import CustomDropdown, { DropdownOption } from "../components/CustomDropdown";
import { SidePanel } from "../components/SidePanel";
import { UploadFormPanelNew } from "../components/UploadFormPanelNew";
import { AnalyticsPanel } from "../components/AnalyticsPanel";
import { AdminDashboardHeader } from "../components/AdminDashboardHeader";
import { AdminDashboardCardsSection } from "../components/AdminDashboardCardsSection";
import { useQueryClient } from "@tanstack/react-query";
import type { UploadResponse } from "../api/endpoints/upload";
import { TemplatesList } from "../components/TemplatesList";
import { useAuth } from "../contexts/AuthContext";

interface AdminDashboardProps {
  onNavigate: (page: string, data?: unknown) => void;
}

export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  // touch prop to satisfy lint (not used on this page yet)
  void onNavigate;

  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
  const [analyticsPanelOpen, setAnalyticsPanelOpen] = useState(false);
  const [pendingFileId, setPendingFileId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const searchMinChars = 3;
  type StatusFilter = "all" | "published" | "draft";
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const statusOptions: DropdownOption[] = [
    { value: "all", label: "All" },
    { value: "published", label: "Published" },
    { value: "draft", label: "Draft" },
  ];

  // After upload success: invalidate templates and highlight the uploaded item
  const handleUploadSuccess = React.useCallback(
    (response: UploadResponse) => {
      setUploadPanelOpen(false);
      setPendingFileId(response.fileId);

      void queryClient.invalidateQueries({
        queryKey: ["templates"],
      });
    },
    [queryClient]
  );

  return (
    <>
      <div className={`overflow-auto ${!isAdmin ? ' max-w-7xl mx-auto p-6 space-y-6' : ''}`}>
        {/* <AdminDashboardHeader /> */}

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Assessment Forms</h1>
            <p className="text-gray-600 mt-1">
              Manage state assessment templates and forms
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-3 mb-8">
              <button
                onClick={() => setUploadPanelOpen(true)}
                className="btn-primary"
              >
                <Upload className="w-4 h-4" />
                Upload New Form
              </button>
              <button
                onClick={() => setAnalyticsPanelOpen(true)}
                className="btn-secondary"
              >
                <BarChart3 className="w-4 h-4" />
                View Analytics
              </button>
            </div>
          )}
        </div>

        <AdminDashboardCardsSection />

        <div className="flex flex-col gap-6">
          {isAdmin && (
            <div className="flex gap-3">
              <div className="flex-1">
                <SearchInput
                  value={query}
                  onChange={setQuery}
                  placeholder="Search assessments..."
                  minChars={searchMinChars}
                />
              </div>
              <CustomDropdown
                options={statusOptions}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v as StatusFilter)}
                placeholder="Filter by status"
                className="mt-0 h-9"
                searchMinChars={0}
                showSearch={false}
                buttonClass="!h-[41px] !w-24"
              />
            </div>
          )}

          <TemplatesList
            pendingUploadedFileId={pendingFileId}
            onPendingFileIdConsumed={() => setPendingFileId(null)}
            searchQuery={query.length >= searchMinChars ? query : ""}
            statusFilter={statusFilter}
          />
        </div>
      </div>

      {isAdmin && (
        <SidePanel
          isOpen={uploadPanelOpen}
          onClose={() => setUploadPanelOpen(false)}
          title="Upload New Form"
          size="md"
        >
          {/* <UploadFormPanel onSuccess={() => setUploadPanelOpen(false)} /> */}
          <UploadFormPanelNew onSuccess={handleUploadSuccess} />
        </SidePanel>
      )}

      {isAdmin && (
        <SidePanel
          isOpen={analyticsPanelOpen}
          onClose={() => setAnalyticsPanelOpen(false)}
          title="Global Analytics"
          size="md"
        >
          <AnalyticsPanel />
        </SidePanel>
      )}
    </>
  );
}
